import type { SnowmentUnit } from '@snowit/utils/snowmentv2';
import type { StorageEntry } from './async';

import snowment from '@snowit/utils/snowmentv2';

const VERSION = process.env.SNOWIT_VERSION_HASHED || '1.0.0';

type GetOptions = {
  excludeVersion?: boolean;
  excludeDate?: boolean;
};

type SetOptions = {
  expiration?: number;
  expirationUnits?: SnowmentUnit;
  forever?: boolean;
};

const getStorage = (name: string): StorageEntry | undefined => {
  try {
    const item = localStorage.getItem(name);
    if (!item) return undefined;
    return JSON.parse(item);
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

const isExpired = (entry: StorageEntry, opt: GetOptions = {}): boolean => {
  if (entry.forever) return false;
  const { expiration, version } = entry;
  const { excludeDate, excludeVersion } = opt;
  const expVersion = !excludeVersion && version !== VERSION;
  const expDate = !excludeDate && snowment().isAfter(expiration, 'day');
  return expVersion || expDate;
};

const exists = <T>(name: string, fallback: T | undefined): T | undefined => {
  try {
    const storage = getStorage(name);
    if (!storage) return fallback;
    if (isExpired(storage)) return fallback;

    return storage.data as T;
  } catch (error) {
    console.error(error);
    return fallback;
  }
};

export const getLocal = <T>(name: string, fallback?: T) => exists<T>(name, fallback);

export const setLocal = <T>(name: string, data: T, opt: SetOptions = {}) => {
  const storage = {
    data,
    version: VERSION,
    forever: opt.forever,
    expiration: snowment()
      .add(opt.expiration ?? 365, opt.expirationUnits ?? 'y')
      .libFormat('YYYY-MM-DD HH:mm:ss'),
  };
  localStorage.setItem(name, JSON.stringify(storage));
};

export const removeLocal = (name: string) => {
  const storage = getLocal(name);
  if (storage) {
    localStorage.removeItem(name);
  }
  return storage;
};

export const clearLocal = () => {
  localStorage.clear();
};
